import { Box } from "@mui/material";
import { runInAction, toJS } from "mobx";
import { observer } from "mobx-react-lite";
import { useEffect, useLayoutEffect, useState } from "react";
import Confetti from "react-confetti";
import { Trans, useTranslation } from "react-i18next";
import { Outlet, useLocation, useParams } from "react-router-dom";
import TgEvaluationTutorialPopup from "../../../../../components/Popups/TgEvaluationTutorialPopup/TgEvaluationTutorialPopup";
import TgEvolvePopup from "../../../../../components/Popups/TgEvolvePopup/TgEvolvePopup";
import RouterConstants from "../../../../../constants/RouterConstants";
import { TG_FEEDBACKS_TO_EVOLVE } from "../../../../../constants/TgConstants";
import { USER_SETTINGS } from "../../../../../constants/UserConstants";
import useSecureNavigate from "../../../../../hooks/useSecureNavigate";
import { useAppStore } from "../../../../../hooks/useStores";
import useWindowSize from "../../../../../hooks/useWindowSize";
import { showBottomCenterToast } from "../../../../../utils/toast";
import iframeStore from "../../../../IframeNew/iframe-store";
import { FeedbackTypeEnum } from "../../../../Playground/Images/Overlay/PlaygroundImageOverlay";
import PlaygroundImages from "../../../../Playground/Images/PlaygroundImages";
import { ViewTgProjectLocationState } from "../../../Projects/Viewer/TgProjectViewer";
import { defaultModel } from "../../store/tg-model-store";
import TgModelEvaluationEmptyState from "./EmptyState/TgModelEvaluationEmptyState";
import TgIntegrateModelBanner from "./IntegrateBanner/TgIntegrateModelBanner";
import styles from "./TgModelEvaluation.module.scss";

const TgModelEvaluation = () => {
	const { t } = useTranslation("tg", { keyPrefix: "models.viewer.evaluation" });
	const navigate = useSecureNavigate();
	const location = useLocation();
	const { projectId, modelId } = useParams();
	const { uiStore, tgStore, authStore, playgroundStore, textToImageStore } = useAppStore();
	const { tgProjectStore, tgModelStore, tgModelPlaygroundStore } = tgStore;
	const [openEvolvePopup, setOpenEvolvePopup] = useState(false);
	const [showConfetti, setShowConfetti] = useState(false);
	const { width, height } = useWindowSize();

	// Get the currently selected model from the evaluation config
	const currentModel =
		tgProjectStore.paginatedModels.items.find(
			(m) => m.id.toString() === textToImageStore.config.tailored_model_id,
		) ?? tgModelStore.modelToView;

	useLayoutEffect(() => {
		tgModelPlaygroundStore.resetPlayground();
	}, []);

	useEffect(() => {
		(async () => {
			const modelToView = location.state?.modelToView || tgModelStore.modelToView;
			if (!modelToView.id && modelId) {
				const model = await tgModelStore.getModel(Number(modelId));
				runInAction(() => {
					tgModelStore.modelToView = model;
				});
			}
		})();

		return () => {
			tgModelStore.modelToView = defaultModel;
			window.history.replaceState({}, "");
		};
	}, []);

	useEffect(() => {
		uiStore.hideSideBar();

		(async () => {
			if (projectId) {
				const viewProjectState: ViewTgProjectLocationState = {
					projectToView: toJS(tgProjectStore.projectToView),
				};

				const fromLegacy = !!location.state?.fromLegacy;
				const base = fromLegacy
					? RouterConstants.NEW_TG.LEGACY.PROJECTS.fullPath
					: RouterConstants.NEW_TG.PROJECTS.fullPath;

				uiStore.showBackButton(
					`${base}/${projectId}/${RouterConstants.NEW_TG.MODELS.path}`,
					{ state: viewProjectState },
					t(`backButton`, { projectName: tgProjectStore.projectToView.project_name || "Project" }),
				);

				if (isNaN(tgProjectStore.projectToView.id)) {
					const project = await tgProjectStore.getProject(Number(projectId));
					tgProjectStore.projectToView = project;
				}

				showTgEvaluationTutorial();
			}
		})();

		return () => {
			tgModelPlaygroundStore.resetPlayground();
			uiStore.showSideBar();
			uiStore.hideBackButton();
		};
	}, []);

	useEffect(() => {
		if (iframeStore.isIframe()) return;

		const feedback = playgroundStore.lastFeedback;
		if (!feedback) return;

		// Update the feedbackReport with the latest data from the response
		const feedbackReport = feedback.response.feedback_report;
		tgModelStore.feedbackReport = feedbackReport;

		if (feedback.feedbackType === FeedbackTypeEnum.Positive) {
			const positiveCount = feedbackReport.positive;
			if (positiveCount < TG_FEEDBACKS_TO_EVOLVE) {
				showBottomCenterToast(
					<Trans
						t={t}
						i18nKey={
							feedback?.response.feedback_report.positive === TG_FEEDBACKS_TO_EVOLVE - 1
								? "feedbackGamification.9positive"
								: "feedbackGamification.under10Positive"
						}
						values={{
							count: feedback?.response.feedback_report.positive,
							feedbacksToEvolve: TG_FEEDBACKS_TO_EVOLVE,
						}}
						components={{ strong: <strong /> }}
					/>,
					"tg-evaluation-feedback-toast",
				);
			} else if (positiveCount === TG_FEEDBACKS_TO_EVOLVE) {
				setShowConfetti(true);
				setOpenEvolvePopup(true);
			}
		}
		playgroundStore.lastFeedback = null;
	}, [playgroundStore.lastFeedback]);

	useEffect(() => {
		if (!openEvolvePopup) setShowConfetti(false);
	}, [openEvolvePopup]);

	const showTgEvaluationTutorial = () => {
		if (authStore.user?.settings?.[USER_SETTINGS.hideTgEvaluationTutorial]) return;
		uiStore.showDialog("TgEvaluationTutorialDialog");
	};

	const handleEvolve = () => {
		setOpenEvolvePopup(false);
		const viewProjectState: ViewTgProjectLocationState = {
			projectToView: toJS(tgProjectStore.projectToView),
			modelToEvolve: toJS(currentModel),
		};
		navigate(`${RouterConstants.NEW_TG.PROJECTS.fullPath}/${projectId}/${RouterConstants.NEW_TG.MODELS.path}`, {
			state: viewProjectState,
		});
	};

	return (
		<Box className={styles.root}>
			<Outlet />
			<Box className={styles.imagesContainer}>
				<PlaygroundImages emptyState={<TgModelEvaluationEmptyState />} />
				{!iframeStore.isIframe() && <TgIntegrateModelBanner />}
			</Box>
			<TgEvaluationTutorialPopup />
			<TgEvolvePopup
				open={openEvolvePopup}
				onClose={() => setOpenEvolvePopup(false)}
				model={currentModel}
				feedbacksCount={currentModel.feedback_count?.positive ?? TG_FEEDBACKS_TO_EVOLVE}
				onEvolve={handleEvolve}
			/>
			{showConfetti && <Confetti width={width} height={height} wind={0.01} />}
		</Box>
	);
};

export default observer(TgModelEvaluation);
